import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "../../components/Icon";
import { IconButton } from "../../components/IconButton";
import type { PageDTO, PageFileDTO, PageViewDTO } from "../../../shared/types";
import { PAGE_SANDBOX } from "../../../shared/types";
import { PageVisibilityBadge } from "../../components/PageVisibility";
import { formatBytes } from "../../components/Files";
import { confirmDialog, Dialog, promptDialog } from "../../components/Dialog";
import { Table } from "../../components/Table";
import { Tabs, useTabParam } from "../../components/Tabs";
import { TextLink } from "../../components/TextLink";
import { Button, ButtonLink, ErrorText, Field, inputCls, PageHeader, timeAgo } from "../../components/ui";
import { ApiError, del, get, patch, post } from "../../lib/api";
import { navigate } from "../../lib/router";

const TEXT = /\.(html?|css|m?js|json|map|txt|md|csv|xml|svg|webmanifest|ts|tsx|jsx|yml|yaml)$/i;
const IMAGE = /\.(png|jpe?g|gif|webp|avif|ico|svg)$/i;

const fileApi = (pageId: string, path: string) => `/api/pages/${pageId}/file?path=${encodeURIComponent(path)}`;

async function send(url: string, init: RequestInit): Promise<any> {
  const res = await fetch(url, { credentials: "same-origin", ...init });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `HTTP ${res.status}`);
  return data;
}

export function AdminPages({ pageId }: { pageId?: string }) {
  return pageId ? <PageDetail pageId={pageId} /> : <PageList />;
}

function PageList() {
  const [pages, setPages] = useState<PageDTO[]>([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => get<PageDTO[]>(`/api/pages${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(setPages, () => {}), 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <PageHeader
        title="Pages"
        subtitle="Hosted web pages built by agents (e.g. Designer). Each page's owner decides who may open it; you open the ones shared with you."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>New page</Button>}
      />
      <div className="px-4 py-4 md:px-6">
        <input className={`${inputCls} mb-3 max-w-sm`} placeholder="Search title or notes…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Table
          rows={pages}
          rowKey={(p) => p.id}
          onRowClick={(p) => navigate(`/admin/pages/${p.id}`)}
          empty="No pages yet. Ask Designer in chat, or create one here."
          columns={[
            {
              header: "Preview",
              hideOnPhone: true,
              className: "w-40",
              cell: (p) => (
                <>
                  {/* A public page's generated preview, the same image its links unfurl with. Nothing for the others. */}
                  {p.visibility === "public" ? (
                    <img
                      src={`${p.url}og.png?v=${Date.parse(p.updatedAt) || 0}`}
                      alt=""
                      loading="lazy"
                      className="aspect-[1200/630] w-32 max-w-full rounded-lg bg-haze-soft object-cover ring-1 ring-line"
                    />
                  ) : (
                    <div className="grid aspect-[1200/630] w-32 place-items-center rounded-lg bg-fill-subtle text-tiny text-faint ring-1 ring-line">
                      {p.visibility === "everyone" ? "everyone" : "private"}
                    </div>
                  )}
                </>
              ),
            },
            {
              header: "Title",
              primary: true,
              cell: (p) => (
                <>
                  <div className="truncate font-medium">{p.title}</div>
                  <div className="truncate text-xs text-muted">{p.description.split("\n")[0]}</div>
                </>
              ),
            },
            { header: "Owner", className: "text-muted", cell: (p) => p.ownerName ?? p.ownerUserId.slice(0, 8) },
            { header: "Agent", className: "text-xs text-muted", cell: (p) => p.updatedByAgentSlug ?? p.createdByAgentSlug ?? "admin" },
            { header: "Who can open it", cell: (p) => <PageVisibilityBadge visibility={p.visibility} /> },
            { header: "Updated", className: "text-xs text-muted", cell: (p) => timeAgo(p.updatedAt) },
            {
              header: "",
              align: "right",
              cell: (p) => <TextLink href={p.url} newTab onClick={(e) => e.stopPropagation()} className="text-xs">open ↗</TextLink>,
            },
          ]}
        />
      </div>
      <PageDetailsModal
        open={creating}
        onClose={() => setCreating(false)}
        onSave={async (v) => {
          const page = await post<PageDTO>("/api/pages", v);
          navigate(`/admin/pages/${page.id}`);
        }}
      />
    </div>
  );
}

function PageDetailsModal({ open, onClose, onSave, page }: { open: boolean; onClose: () => void; onSave: (v: { title: string; description: string }) => Promise<void>; page?: PageDTO }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTitle(page?.title ?? "");
    setDescription(page?.description ?? "");
    setError(null);
  }, [open, page]);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSave({ title, description });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={page ? "Edit page details" : "New page"}
      busy={busy}
      primary={<Button variant="primary" disabled={busy || !title.trim()} onClick={save}>{page ? "Save" : "Create"}</Button>}
    >
      <div className="space-y-3">
        <Field label="Title">
          <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </Field>
        <Field label="Notes" hint="Brief, structure and open to-dos. Agents read and update this to continue work on the page.">
          <textarea className={`${inputCls} h-56 font-mono text-xs`} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <ErrorText error={error} />
      </div>
    </Dialog>
  );
}

type TreeNode = { name: string; path: string; children: Map<string, TreeNode>; file?: PageFileDTO };

function buildTree(files: PageFileDTO[]): TreeNode {
  const root: TreeNode = { name: "", path: "", children: new Map() };
  for (const f of files) {
    let node = root;
    const parts = f.path.split("/");
    parts.forEach((part, i) => {
      const path = parts.slice(0, i + 1).join("/");
      if (!node.children.has(part)) node.children.set(part, { name: part, path, children: new Map() });
      node = node.children.get(part)!;
      if (i === parts.length - 1) node.file = f;
    });
  }
  return root;
}

function Tree({ node, depth, selected, collapsed, onToggle, onSelect, onDeleteFolder }: {
  node: TreeNode; depth: number; selected: string | null; collapsed: Set<string>;
  onToggle: (path: string) => void; onSelect: (f: PageFileDTO) => void; onDeleteFolder?: (path: string) => void;
}) {
  const entries = [...node.children.values()].sort((a, b) => Number(Boolean(a.file)) - Number(Boolean(b.file)) || a.name.localeCompare(b.name));
  return (
    <>
      {entries.map((child) =>
        child.file ? (
          <button
            key={child.path}
            onClick={() => onSelect(child.file!)}
            style={{ paddingLeft: `${0.5 + depth * 0.9}rem` }}
            className={`flex w-full items-center gap-1.5 rounded py-1 pr-2 text-left text-sm ${selected === child.path ? "bg-fill font-medium" : "hover:bg-fill-subtle"}`}
          >
            <Icon name={IMAGE.test(child.name) ? "image" : TEXT.test(child.name) ? "file" : "paperclip"} className="size-3.5 shrink-0 text-faint" />
            <span className="min-w-0 flex-1 truncate">{child.name}</span>
            <span className="shrink-0 text-tiny text-faint">{formatBytes(child.file.size)}</span>
          </button>
        ) : (
          <div key={child.path}>
            <div className="group flex items-center rounded hover:bg-fill-subtle" style={{ paddingLeft: `${0.5 + depth * 0.9}rem` }}>
              <button onClick={() => onToggle(child.path)} className="flex min-w-0 flex-1 items-center gap-1.5 py-1 text-left text-sm">
                <span className={`inline-block text-tiny text-faint transition-transform ${collapsed.has(child.path) ? "" : "rotate-90"}`}>▶</span>
                <Icon name="files" className="size-3.5 shrink-0 text-faint" />
                <span className="truncate">{child.name}</span>
              </button>
              {onDeleteFolder && <IconButton label="Delete folder" size="sm" fill="none" onClick={() => onDeleteFolder(child.path)} className="opacity-0 group-hover:opacity-100">
                <Icon name="close" className="size-3" />
              </IconButton>}
            </div>
            {!collapsed.has(child.path) && (
              <Tree node={child} depth={depth + 1} selected={selected} collapsed={collapsed} onToggle={onToggle} onSelect={onSelect} onDeleteFolder={onDeleteFolder} />
            )}
          </div>
        ),
      )}
    </>
  );
}

function PageDetail({ pageId }: { pageId: string }) {
  const [page, setPage] = useState<PageDTO | null>(null);
  const [files, setFiles] = useState<PageFileDTO[]>([]);
  const [selected, setSelected] = useState<PageFileDTO | null>(null);
  const [content, setContent] = useState<string>("");
  const [original, setOriginal] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [view, setView] = useTabParam(["files", "preview", "notes"] as const);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  // Where the content is framed from, for whoever may open it; null when this page is not shared with you.
  const [frame, setFrame] = useState<{ url: string | null; viewable: boolean } | null>(null);
  const [folder, setFolder] = useState("");
  const uploadRef = useRef<HTMLInputElement>(null);
  const folderUploadRef = useRef<HTMLInputElement>(null);

  // Only the page's owner changes it and reads its notes (D1): the controls follow.
  const [may, setMay] = useState({ edit: false });

  const load = useCallback(async () => {
    try {
      const r = await get<{ page: PageDTO; files: PageFileDTO[]; canEdit: boolean }>(`/api/pages/${pageId}`);
      setPage(r.page);
      setMay({ edit: r.canEdit });
      setFiles(r.files);
      return r.files;
    } catch (err) {
      setError((err as Error).message);
      return [];
    }
  }, [pageId]);

  useEffect(() => {
    void Promise.all([
      load(),
      get<PageViewDTO>(`/api/pages/${pageId}/view`).then((v) => ({ url: v.frameUrl, viewable: true }), () => ({ url: null, viewable: false })),
    ]).then(([list, f]) => {
      setFrame(f);
      const index = list.find((x) => x.path === "index.html") ?? list[0];
      if (index && f.viewable) void open(index);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => () => void (imageUrl && URL.revokeObjectURL(imageUrl)), [imageUrl]);

  const dirty = selected !== null && TEXT.test(selected.path) && content !== original;
  const tree = useMemo(() => buildTree(files), [files]);

  async function open(f: PageFileDTO) {
    if (dirty && !(await confirmDialog({ title: "Discard unsaved changes?", body: `Your edits to ${selected?.path} are lost.`, confirm: "Discard", danger: true }))) return;
    setError(null);
    setSelected(f);
    setImageUrl(null);
    setFolder(f.path.includes("/") ? f.path.slice(0, f.path.lastIndexOf("/")) : "");
    const res = await fetch(fileApi(pageId, f.path), { credentials: "same-origin" });
    if (!res.ok) {
      setError(`Could not load ${f.path} (HTTP ${res.status})`);
      return;
    }
    if (TEXT.test(f.path)) {
      const text = await res.text();
      setContent(text);
      setOriginal(text);
    } else if (IMAGE.test(f.path)) {
      setImageUrl(URL.createObjectURL(await res.blob()));
    }
  }

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const save = () =>
    run(async () => {
      if (!selected) return;
      await send(fileApi(pageId, selected.path), { method: "PUT", body: content });
      setOriginal(content);
      await load();
      setPreviewKey((k) => k + 1);
    });

  const newFile = () =>
    run(async () => {
      const path = await promptDialog({ title: "New file", label: "Path", body: "Folders in the path are created automatically.", value: folder ? `${folder}/new-file.html` : "new-file.html", confirm: "Create file" });
      if (!path) return;
      await send(fileApi(pageId, path), { method: "PUT", body: "" });
      const list = await load();
      const created = list.find((f) => f.path === path.replace(/^\/+/, ""));
      if (created) await open(created);
    });

  const rename = () =>
    run(async () => {
      if (!selected) return;
      const to = await promptDialog({ title: `Move or rename ${selected.path}`, label: "New path", value: selected.path, confirm: "Move" });
      if (!to || to === selected.path) return;
      await post(`/api/pages/${pageId}/move`, { from: selected.path, to });
      const list = await load();
      const moved = list.find((f) => f.path === to.replace(/^\/+/, ""));
      if (moved) await open(moved);
    });

  const remove = (path: string, isFolder = false) =>
    run(async () => {
      if (!(await confirmDialog({ title: `Delete ${path}?`, body: isFolder ? "The folder and everything in it are deleted." : "The file is deleted from the page.", confirm: isFolder ? "Delete folder" : "Delete file", danger: true }))) return;
      await send(fileApi(pageId, path), { method: "DELETE" });
      if (selected && (selected.path === path || selected.path.startsWith(`${path}/`))) {
        setSelected(null);
        setContent("");
        setOriginal("");
      }
      await load();
      setPreviewKey((k) => k + 1);
    });

  const upload = (list: FileList | null, keepFolders: boolean) =>
    run(async () => {
      if (!list?.length) return;
      const form = new FormData();
      form.append("dir", folder);
      for (const f of Array.from(list)) {
        form.append("path", keepFolders && f.webkitRelativePath ? f.webkitRelativePath : f.name);
        form.append("file", f, f.name);
      }
      await send(`/api/pages/${pageId}/upload`, { method: "POST", body: form });
      await load();
      setPreviewKey((k) => k + 1);
    });

  const deletePage = () =>
    run(async () => {
      if (!(await confirmDialog({ title: `Delete "${page?.title}"?`, body: "The page and all its files are deleted, and its link stops working.", confirm: "Delete page", danger: true }))) return;
      await del(`/api/pages/${pageId}`);
      navigate("/admin/pages");
    });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s" && view === "files" && dirty) {
        e.preventDefault();
        void save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!page) return <div className="p-8 text-sm text-muted">{error ?? "Loading…"}</div>;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title={page.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <TextLink to="/admin/pages">← Pages</TextLink>
            <span>by {page.ownerName ?? page.ownerEmail}</span>
            {page.createdByAgentSlug && <span>created by {page.createdByAgentSlug}</span>}
            <span>updated {timeAgo(page.updatedAt)}{page.updatedByAgentSlug ? ` by ${page.updatedByAgentSlug}` : ""}</span>
            {page.conversationId && <TextLink to={`/admin/conversations/${page.conversationId}`}>conversation</TextLink>}
            <PageVisibilityBadge visibility={page.visibility} />
          </span>
        }
        actions={
          <>
            <ButtonLink href={page.url}>Open ↗</ButtonLink>
            <Button onClick={() => void navigator.clipboard?.writeText(page.url)}>Copy link</Button>
            {may.edit && <Button onClick={() => setEditing(true)}>Edit details</Button>}
            {/* Deleting a page is administration (a departed person's stale page): any admin; changing it is its owner's. */}
            <Button variant="danger" onClick={deletePage}>Delete</Button>
          </>
        }
      />
      <div className="flex items-center gap-1 border-b border-line px-4 py-2 md:px-6">
        <Tabs
          label="Page view"
          value={view}
          onChange={setView}
          tabs={[
            { key: "files", label: "Files", count: files.length },
            { key: "preview", label: "Preview" },
            { key: "notes", label: "Notes" },
          ]}
        />
        <span className="ml-auto truncate font-mono text-xs text-faint">{page.url}</span>
      </div>
      {error && <div className="px-4 pt-2 md:px-6"><ErrorText error={error} /></div>}
      {frame && !frame.viewable && (
        <div className="px-4 pt-2 text-xs text-muted md:px-6">This page is not shared with you: you see its files listed, not what is in them. Its owner shares it on the page.</div>
      )}
      {frame?.viewable && !may.edit && (
        <div className="px-4 pt-2 text-xs text-muted md:px-6">Only {page.ownerName ?? "its owner"} changes this page: you read it here.</div>
      )}

      {view === "files" && (
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <aside className="flex max-h-72 shrink-0 flex-col border-b border-line md:max-h-none md:w-72 md:border-r md:border-b-0">
            {may.edit && (
              <div className="flex flex-wrap gap-1 border-b border-line p-2">
                <Button variant="ghost" size="sm" onClick={newFile}>+ File</Button>
                <Button variant="ghost" size="sm" onClick={() => uploadRef.current?.click()}>Upload</Button>
                <Button variant="ghost" size="sm" onClick={() => folderUploadRef.current?.click()}>Upload folder</Button>
                <input ref={uploadRef} type="file" multiple hidden onChange={(e) => { void upload(e.target.files, false); e.target.value = ""; }} />
                <input
                  ref={folderUploadRef}
                  type="file"
                  multiple
                  hidden
                  {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
                  onChange={(e) => { void upload(e.target.files, true); e.target.value = ""; }}
                />
              </div>
            )}
            {may.edit && (
              <div className="px-3 pt-2 text-tiny text-faint">
                Uploads go to <span className="font-mono">/{folder}</span>
                {folder && <TextLink className="ml-1" onClick={() => setFolder("")}>root</TextLink>}
              </div>
            )}
            <div className="scroll-thin min-h-0 flex-1 overflow-auto p-1">
              <Tree
                node={tree}
                depth={0}
                selected={selected?.path ?? null}
                collapsed={collapsed}
                onToggle={(path) => {
                  setFolder(path);
                  setCollapsed((s) => {
                    const next = new Set(s);
                    if (next.has(path)) next.delete(path);
                    else next.add(path);
                    return next;
                  });
                }}
                onSelect={(f) => void open(f)}
                onDeleteFolder={may.edit ? (path) => void remove(path, true) : undefined}
              />
              {files.length === 0 && <div className="p-3 text-xs text-faint">No files.</div>}
            </div>
          </aside>
          <section className="flex min-h-0 min-w-0 flex-1 flex-col">
            {selected ? (
              <>
                <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
                  <span className="min-w-0 flex-1 truncate font-mono text-xs">
                    {selected.path}
                    {dirty && <span className="ml-1 text-warning">● unsaved</span>}
                  </span>
                  <span className="text-xs text-faint">{selected.mime} · {formatBytes(selected.size)}</span>
                  {may.edit && TEXT.test(selected.path) && <Button variant="primary" size="sm" disabled={!dirty} onClick={save}>Save</Button>}
                  {frame?.url && <TextLink href={`${frame.url}${selected.path}`} newTab className="text-xs">open ↗</TextLink>}
                  {may.edit && <Button size="sm" onClick={rename}>Rename</Button>}
                  {may.edit && <Button variant="danger" size="sm" onClick={() => remove(selected.path)}>Delete</Button>}
                </div>
                {TEXT.test(selected.path) ? (
                  <textarea
                    value={content}
                    readOnly={!may.edit}
                    onChange={(e) => setContent(e.target.value)}
                    spellCheck={false}
                    className="min-h-[24rem] flex-1 resize-none bg-surface p-3 font-mono text-xs leading-relaxed outline-none"
                  />
                ) : imageUrl ? (
                  <div className="flex flex-1 items-center justify-center overflow-auto bg-fill p-4">
                    <img src={imageUrl} alt={selected.path} className="max-h-full max-w-full object-contain" />
                  </div>
                ) : (
                  <div className="p-6 text-sm text-muted">Binary file. Open it in a new tab{may.edit ? " or replace it with Upload" : ""}.</div>
                )}
              </>
            ) : (
              <div className="p-6 text-sm text-muted">Select a file to {may.edit ? "view or edit" : "view"} it.</div>
            )}
          </section>
        </div>
      )}

      {view === "preview" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center gap-2 px-4 py-2 md:px-6">
            <Button size="sm" onClick={() => setPreviewKey((k) => k + 1)}>Reload</Button>
            {frame && !frame.url && <span className="text-xs text-muted">{frame.viewable ? "No pages host is set up here." : "Not shared with you."}</span>}
          </div>
          {frame?.url && (
            <iframe
              key={previewKey}
              src={frame.url}
              title="Page preview"
              sandbox={PAGE_SANDBOX}
              referrerPolicy="no-referrer"
              className="min-h-[32rem] w-full flex-1 border-t border-line bg-white"
            />
          )}
        </div>
      )}

      {view === "notes" && (
        <div className="scroll-thin flex-1 overflow-auto px-4 py-4 md:px-6">
          <pre className="text-sm whitespace-pre-wrap">
            {page.description || (may.edit ? "No notes yet. Agents keep the brief and to-dos here." : "Its notes are not shared with you: they are the working brief of whoever may change the page.")}
          </pre>
        </div>
      )}

      <PageDetailsModal
        open={editing}
        page={page}
        onClose={() => setEditing(false)}
        onSave={async (v) => setPage(await patch<PageDTO>(`/api/pages/${pageId}`, v))}
      />
    </div>
  );
}
